import { ICustomerService } from '../../ng-app/customer/ICustomerService';
import { LocationSummaryService } from '../../ng-app/locationSummary/LocationSummaryService';
import { ZipValidationService } from '../../ng-app/zipValidator/ZipValidatorService';
import { EditMetadata } from '../directives/EditableContent/models';
import { Country, State } from '../typings/api.location';
import { AppRootScope } from '../typings/AppRootScope';
import { EventTypes, LocationEdit } from '../typings/models';
import { ICommonLocationService } from '../typings/services';

/**
 * Common 'Location Information' form that can be shared between settings (edit) & registration (new)
 */

export class LocationInformationFormComponent implements angular.IComponentOptions {
    public static $name: string = 'locationInformationForm';

    // Todo: convert to one-way + events
    public bindings: any = {
        isAdding: '=?',
        isControlsEnabled: '<?',
        isDisabled: '<?',
        ngModel: '=',
    };

    public controller = LocationInformationFormController;

    public template: string = require('!!html-loader!./LocationInformationFormView.html');
}

// tslint:disable-next-line:max-classes-per-file
export class LocationInformationFormController implements angular.IComponentController {
    public static $inject: string[] = [
        '$scope',
        'commonLocationService',
        'customerService',
        'zipValidationService',
        'locationSummaryService'
    ];

    public ngModel: LocationEdit;
    public editMetadata: EditMetadata;
    public formName: string = 'locationInformation';
    private isDisabled: boolean;
    private isAdding: boolean;
    private isControlsEnabled: boolean = true;
    public canEditIsCertified: boolean;
    public canEditIsAirQualityExcellence: boolean;

    private zipValidator: RegExp;

    constructor(
        private $scope: AppRootScope,
        private commonLocationService: ICommonLocationService,
        private customerService: ICustomerService,
        private zipValidationService: ZipValidationService,
        private locationSummaryService: LocationSummaryService
    ) {
    }

    public $onInit() {
        this.initMetadata();

        this.InitPermissions();

        if (this.ngModel) {
            this.populateCountries();
            this.populateCustomers();
        }

        this.$scope.$watch(() => this.ngModel && this.ngModel.location && this.ngModel.location.countryId,
            (value) => {
                if (value) {
                    if (this.isDisabled) {
                        this.getStates();
                    }

                    this.populateCountries();
                    this.populateCustomers();

                    if (this.ngModel.location) {
                        // load states if prefilled
                        if (this.ngModel.location.state || this.ngModel.location.countryId) {
                            this.getStates();
                        }

                        if (this.ngModel.location.zipValidator) {
                            this.updateZipValidatorProperties(this.ngModel.location.zipValidator);
                        }
                    }
                }
            });
    }

    private InitPermissions() {
        if (this.ngModel == null || this.ngModel.canEditIsCertified == null) {
            this.locationSummaryService.getCanManageIsCertified().then(result => {
                this.canEditIsCertified = result;
            });
        } else {
            this.canEditIsCertified = this.ngModel.canEditIsCertified;
        }

        if (this.ngModel == null || this.ngModel.canEditIsAirQualityExcellence == null) {
            this.locationSummaryService.getCanManageIsAirQualityExcellence().then(result => {
                this.canEditIsAirQualityExcellence = result;
            });
        } else {
            this.canEditIsAirQualityExcellence = this.ngModel.canEditIsAirQualityExcellence;
        }
    }

    private countrySelected() {
        this.getStates(true);
        this.getZipValidatorForCountry();
    }

    private getStates(clearState?: boolean) {
        this.commonLocationService.getStatesForCountry(this.ngModel.location.countryId)
            .then((states: State[]) => {
                if (clearState) {
                    this.ngModel.location.state = '';
                }
                this.ngModel.states = states;
            });
    }

    private getZipValidatorForCountry() {
        this.zipValidationService.getZipValidator(this.ngModel.location.countryId)
            .then((validator: CASLink.Infrastructure.Ui.ViewModel.ZipValidationViewModel) => {
                this.updateZipValidatorProperties(validator);

                this.$scope[this.formName]['zip'].$validate();
            });
    }

    private initMetadata() {
        const zipValidatorKey = 'zipValidator';

        this.$scope[zipValidatorKey] = {
            test: (val) => {
                if (!this.zipValidator) {
                    return true;
                }

                return this.zipValidator.test(val);
            }
        };

        // tslint:disable: object-literal-sort-keys
        this.editMetadata = {
            editContext: 'Location',
            fieldMetadata: {
                description: {
                    fieldName: 'Location Description',
                    required: true
                },
                city: {
                    fieldName: 'City',
                    maxLength: 50,
                    required: true
                },
                street1: {
                    fieldName: 'Street',
                    maxLength: 60,
                    required: true
                },
                zip: {
                    fieldName: 'Zip',
                    pattern: zipValidatorKey,
                    patternMsg: '{{ $ctrl.ngModel.location.zipValidator.patternMessage }}',
                    required: true
                },
                country: {
                    fieldName: 'Country',
                    required: true
                },
                state: {
                    fieldName: 'State',
                    required: '$ctrl.ngModel.states.length > 0'
                },
                region: {
                    fieldName: 'Region',
                    required: '$ctrl.ngModel.states.length === 0'
                }
            }
        };
        // tslint:enable: object-literal-sort-keys
    }

    private populateCountries() {
        this.commonLocationService.getCountries()
            .then((countries: Country[]) => {
                this.ngModel.countries = countries;
            });
    }

    private populateCustomers() {
        if (this.ngModel.canEditCustomer) {
            this.customerService.getCustomers()
                .then(result => {
                    this.ngModel.customers = result;

                    if (this.ngModel.location.customer) {
                        this.preselectCustomer(this.ngModel.location.customer.id);
                    } else {
                        this.ngModel.location.customer = null;
                    }
                });
        }
    }

    private preselectCustomer(customerId: number) {
        const filteredCustomers = this.ngModel.customers.filter(group => group.id === customerId);

        this.ngModel.location.customer = filteredCustomers.length ? filteredCustomers[0] : null;
    }

    private cancelAdding() {
        this.isAdding = false;
    }

    private addLocation() {
        this.$scope.$emit(EventTypes.LocationAddClicked);
    }

    private updateZipValidatorProperties(validator: CASLink.Infrastructure.Ui.ViewModel.ZipValidationViewModel) {
        this.ngModel.location.zipValidator = validator;

        this.zipValidator = validator ? new RegExp(validator.pattern) : null;
    }
}
